import { useCallback, useEffect, useMemo, useState } from 'react';
import AclAutoDemo from './AclAutoDemo.js';
import ClientLifecycleScreens, {
  CLIENT_LIFECYCLE_SCREENS,
  type ClientLifecycleScreenId,
} from './ClientLifecycleScreens.js';
import OperatorConsole from './OperatorConsole.js';
import {
  clientModeForWorkspace,
  DEFAULT_WORKSPACE_ID,
  INITIAL_WORKSPACES,
  type ActivationState,
  type ClientMode,
  type ClientWorkspace,
  type PreviewMode,
  type PreviewRole,
} from './previewModel.js';

const MODES: { id: PreviewMode; label: string }[] = [
  { id: 'operator', label: 'Operator' },
  { id: 'demo', label: 'Demo client' },
  { id: 'live', label: 'Live client' },
];

interface UrlPreviewState {
  role: PreviewRole;
  clientMode: ClientMode;
  workspaceId: string;
  lifecycleScreen: ClientLifecycleScreenId | null;
}

function previewStateFromUrl(): UrlPreviewState {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const requestedClient = params.get('client');
  const requestedScreen = params.get('screen');
  const workspaceId = INITIAL_WORKSPACES.some((workspace) => workspace.id === requestedClient)
    ? requestedClient as string
    : DEFAULT_WORKSPACE_ID;
  return {
    role: mode === 'demo' || mode === 'live' ? 'client' : 'operator',
    clientMode: mode === 'live' ? 'live' : 'demo',
    workspaceId,
    lifecycleScreen: CLIENT_LIFECYCLE_SCREENS.some((screen) => screen.id === requestedScreen)
      ? requestedScreen as ClientLifecycleScreenId
      : null,
  };
}

function PreviewBar({ mode, lifecycleScreen, setMode, setLifecycleScreen }: {
  mode: PreviewMode;
  lifecycleScreen: ClientLifecycleScreenId | null;
  setMode: (mode: PreviewMode) => void;
  setLifecycleScreen: (screen: ClientLifecycleScreenId | null) => void;
}) {
  const groupedScreens = useMemo(() => [
    { label: 'Signup & activation', screens: CLIENT_LIFECYCLE_SCREENS.filter((screen) => screen.group === 'public') },
    { label: 'Service lifecycle', screens: CLIENT_LIFECYCLE_SCREENS.filter((screen) => screen.group === 'lifecycle') },
    { label: 'Account', screens: CLIENT_LIFECYCLE_SCREENS.filter((screen) => screen.group === 'profile') },
    { label: 'Review states', screens: CLIENT_LIFECYCLE_SCREENS.filter((screen) => screen.group === 'review') },
  ], []);

  return (
    <header className="preview-bar">
      <div className="preview-disclosure" role="note">
        <span/><strong>Internal product review</strong><small>Internal preview · Synthetic data · No provider actions</small>
      </div>
      <div className="preview-mode-switcher" role="group" aria-label="Preview perspective">
        {MODES.map((item) => <button key={item.id} type="button" className={mode === item.id ? 'active' : ''} aria-pressed={mode === item.id} onClick={() => setMode(item.id)}>{item.label}</button>)}
      </div>
      <label className="preview-mode-select"><span>Preview</span><select aria-label="Preview perspective" value={mode} onChange={(event) => setMode(event.target.value as PreviewMode)}>{MODES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
      <label className="preview-lifecycle-select"><span>UX state</span><select aria-label="Review client lifecycle screen" value={lifecycleScreen ?? ''} onChange={(event) => setLifecycleScreen(event.target.value ? event.target.value as ClientLifecycleScreenId : null)}><option value="">Dashboard experience</option>{groupedScreens.map((group) => <optgroup key={group.label} label={group.label}>{group.screens.map((screen) => <option key={screen.id} value={screen.id}>{screen.label}</option>)}</optgroup>)}</select></label>
    </header>
  );
}

export default function AclAutoPreview() {
  const initial = useMemo(previewStateFromUrl, []);
  const [role, setRole] = useState<PreviewRole>(initial.role);
  const [clientMode, setClientMode] = useState<ClientMode>(initial.clientMode);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(initial.workspaceId);
  const [lifecycleScreen, setLifecycleScreen] = useState<ClientLifecycleScreenId | null>(initial.lifecycleScreen);
  const [workspaces, setWorkspaces] = useState<ClientWorkspace[]>(INITIAL_WORKSPACES);
  const [activation, setActivation] = useState<ActivationState>({
    completed: ['source', 'phone', 'messaging', 'voice', 'calendar'],
    activated: false,
  });

  const selectedWorkspace = workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? workspaces[0];
  const mode: PreviewMode = role === 'operator' ? 'operator' : clientMode;

  const navigatePreview = useCallback((nextMode: PreviewMode, workspaceId = selectedWorkspaceId) => {
    const workspace = workspaces.find((candidate) => candidate.id === workspaceId) ?? workspaces[0];
    const nextRole: PreviewRole = nextMode === 'operator' ? 'operator' : 'client';
    const nextClientMode: ClientMode = nextMode === 'operator' ? clientMode : nextMode;
    const url = new URL(window.location.href);
    url.searchParams.set('mode', nextMode);
    url.searchParams.set('client', workspace.id);
    url.searchParams.delete('screen');
    window.history.pushState({ mode: nextMode, client: workspace.id }, '', url);
    setRole(nextRole);
    setClientMode(nextClientMode);
    setSelectedWorkspaceId(workspace.id);
    setLifecycleScreen(null);
    window.scrollTo({ top: 0, left: 0 });
  }, [clientMode, selectedWorkspaceId, workspaces]);

  const navigateLifecycle = useCallback((nextScreen: ClientLifecycleScreenId | null) => {
    const url = new URL(window.location.href);
    if (nextScreen) url.searchParams.set('screen', nextScreen);
    else url.searchParams.delete('screen');
    window.history.pushState({ mode, client: selectedWorkspaceId, screen: nextScreen }, '', url);
    setLifecycleScreen(nextScreen);
    window.scrollTo({ top: 0, left: 0 });
  }, [mode, selectedWorkspaceId]);

  const viewClient = useCallback((workspaceId: string, requestedMode?: ClientMode) => {
    const workspace = workspaces.find((candidate) => candidate.id === workspaceId) ?? workspaces[0];
    navigatePreview(requestedMode ?? clientModeForWorkspace(workspace), workspace.id);
  }, [navigatePreview, workspaces]);

  const toggleActivationItem = useCallback((itemId: string) => {
    setActivation((current) => ({
      ...current,
      completed: current.completed.includes(itemId)
        ? current.completed.filter((id) => id !== itemId)
        : [...current.completed, itemId],
    }));
  }, []);

  const updateWorkspace = useCallback((workspaceId: string, changes: Partial<ClientWorkspace>) => {
    setWorkspaces((current) => current.map((workspace) => workspace.id === workspaceId
      ? {
          ...workspace,
          ...changes,
          paused: changes.lifecycle ? changes.lifecycle === 'Paused' : changes.paused ?? workspace.paused,
        }
      : workspace));
  }, []);

  const activateNorthstar = useCallback(() => {
    setActivation((current) => ({ ...current, activated: true }));
    setWorkspaces((current) => current.map((workspace) => workspace.id === 'northstar'
      ? { ...workspace, lifecycle: 'Live', paused: false, activationDate: '2026-07-09', paidMonths: 1, setupFeePaid: 1_997, mrr: 1_497, billingStatus: 'Active' }
      : workspace));
    navigatePreview('live', 'northstar');
  }, [navigatePreview]);

  useEffect(() => {
    const onPopState = () => {
      const next = previewStateFromUrl();
      setRole(next.role);
      setClientMode(next.clientMode);
      setSelectedWorkspaceId(next.workspaceId);
      setLifecycleScreen(next.lifecycleScreen);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const workspaceStateLabel = selectedWorkspace.paused
    ? 'Paused workspace'
    : clientMode === 'demo' ? 'Demo workspace' : 'Live workspace';

  return (
    <div className={lifecycleScreen ? 'acl-preview-root lifecycle-perspective-root' : role === 'operator' ? 'acl-preview-root' : 'acl-preview-root client-perspective-root'}>
      <PreviewBar mode={mode} lifecycleScreen={lifecycleScreen} setMode={navigatePreview} setLifecycleScreen={navigateLifecycle}/>
      {!lifecycleScreen && role === 'client' && <div className="client-preview-banner" role="region" aria-label="Client perspective preview"><span><strong>{selectedWorkspace.name}</strong><small>{selectedWorkspace.company ? `${selectedWorkspace.company} · ` : ''}Client perspective preview · {workspaceStateLabel} · Not a public demo</small></span><button type="button" onClick={() => navigatePreview('operator')}>Return to operator</button></div>}
      {lifecycleScreen
        ? <ClientLifecycleScreens screen={lifecycleScreen} onNavigate={navigateLifecycle}/>
        : role === 'operator'
        ? <OperatorConsole workspaces={workspaces} updateWorkspace={updateWorkspace} activation={activation} toggleActivationItem={toggleActivationItem} activateNorthstar={activateNorthstar} viewClient={viewClient}/>
        : <AclAutoDemo key={`${selectedWorkspace.id}-${clientMode}-${selectedWorkspace.paused}`} mode={clientMode} workspace={selectedWorkspace}/>
      }
    </div>
  );
}
